import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { fontSizeTokens, spacingTokens } from "../../designTokens";
import { SecondaryButtonMediumV2 } from "../Buttons/Buttons";
import { FiltersIconV2 } from "../Icons/Icons";
import { useStoreState } from "../../util/util";
import type { AdvancedFilterCard } from "../../types/types";
import { AdvancedFiltersModal } from "./AdvancedFiltersModal";
import { useAdvancedFilterCollectionInstances } from "./advancedFiltersUtils";

const ButtonWrapper = styled.div`
  position: relative;
`;

const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background: red;
  color: white;
  border-radius: 50%;
  padding: ${spacingTokens.spacing_xs} ${spacingTokens.spacing_sm};
  font-size: ${fontSizeTokens.font_size_xs};
  line-height: 1;
`;

type AdvancedFiltersButtonProps = {
  /** Current Part 2 criteria, decoded from the page's own filter state — also seeds the modal on open. */
  cards: AdvancedFilterCard[];
  onApply: (cards: AdvancedFilterCard[]) => void;
};

/**
 * Gated on valid configured instance (FE plan §7.2) — renders nothing
 * otherwise, on every insertion point. The
 * collection-instances fetch here shares its SWR key with
 * `AdvancedFiltersBrowsePane`'s own fetch (same `valid_only: true` request),
 * so mounting it here too costs nothing extra.
 */
export const AdvancedFiltersButton = ({
  cards,
  onApply,
}: AdvancedFiltersButtonProps) => {
  const { t } = useTranslation();
  const {
    storefront_id,
    storefront_metadata: { enable_advanced_filters },
  } = useStoreState();
  const [showModal, setShowModal] = useState(false);
  const { instances } = useAdvancedFilterCollectionInstances(storefront_id, {
    enabled: enable_advanced_filters,
  });

  if (!enable_advanced_filters || instances.length === 0) {
    return null;
  }

  return (
    <ButtonWrapper>
      <SecondaryButtonMediumV2
        testid="advanced-filters-button"
        onClick={() => setShowModal(true)}
      >
        <FiltersIconV2 />
        {t("Advanced Filters")}
      </SecondaryButtonMediumV2>
      {cards.length > 0 && <Badge>{cards.length}</Badge>}
      <AdvancedFiltersModal
        show={showModal}
        onClose={() => setShowModal(false)}
        initialCards={cards}
        onApply={onApply}
      />
    </ButtonWrapper>
  );
};

AdvancedFiltersButton.displayName = "AdvancedFiltersButton";
